<template>
    <div class="pa-5 compact-form" v-if="activeCampaign">
        <v-row no-gutters>
            <v-col class="mt-2 row-title" cols="3">{{ t("Name") }}</v-col>
            <v-col cols="6">
                <v-text-field
                    :value="activeCampaign.name"
                    @input="updateField($event, 'name')"
                    class="text-field-item mb-1"
                    hide-details
                    dense
                    outlined
                />
            </v-col>
        </v-row>
        <v-row no-gutters>
            <v-col class="mt-2 row-title" cols="3">{{ t("Description") }}</v-col>
            <v-col cols="6">
                <v-text-field
                    :value="activeCampaign.description"
                    @input="updateField($event, 'description')"
                    class="text-field-item mb-1"
                    hide-details
                    dense
                    outlined
                />
            </v-col>
        </v-row>
        <v-row no-gutters>
            <v-col class="mt-2 row-title" cols="3">{{ t("Info provider") }}</v-col>
            <v-col cols="6">
                <v-select
                    :value="activeCampaign.info_provider"
                    @input="updateField($event, 'info_provider')"
                    placeholder="Select"
                    :items="infoProviders"
                    class="text-field-item mb-1"
                    hide-details
                    dense
                    outlined
                />
            </v-col>
        </v-row>
        <v-row no-gutters v-if="activeCampaign.info_provider === 'odoo'">
            <v-col cols="12">
                <v-row no-gutters>
                    <v-col cols="3" class="mt-2 row-title">{{ t("Export client") }}</v-col>
                    <v-col cols="6">
                        <v-autocomplete
                            :value="activeCampaign.export_client_id"
                            :items="export_clients"
                            :loading="loadingCampaignData"
                            class="text-field-item mb-1"
                            clearable
                            dense
                            hide-details
                            hide-no-data
                            hide-selected
                            item-text="name"
                            item-value="id"
                            outlined
                            @change="updateField($event, 'export_client_id')"
                        />
                    </v-col>
                </v-row>
                <v-row no-gutters>
                    <v-col cols="3" class="mt-2 row-title">{{ t("Client") }}</v-col>
                    <v-col cols="6">
                        <v-autocomplete
                            :value="activeCampaign.client_id"
                            :items="clients"
                            :loading="loadingCampaignData"
                            class="text-field-item mb-1"
                            clearable
                            dense
                            hide-details
                            hide-no-data
                            hide-selected
                            item-text="name"
                            item-value="id"
                            outlined
                            @change="updateField($event, 'client_id')"
                        />
                    </v-col>
                </v-row>
            </v-col>
        </v-row>
        <v-row no-gutters v-else>
            <v-col cols="12">
                <v-row no-gutters>
                    <v-col cols="3" class="mt-2 row-title">{{ t("Store") }}</v-col>
                    <v-col cols="6">
                        <v-autocomplete
                            :value="activeCampaign.store_id"
                            :items="stores"
                            :loading="loadingCampaignData"
                            class="text-field-item mb-1"
                            clearable
                            dense
                            hide-details
                            hide-no-data
                            hide-selected
                            item-text="name"
                            item-value="id"
                            outlined
                            @change="updateField($event, 'store_id')"
                        />
                    </v-col>
                </v-row>
                <v-row no-gutters>
                    <v-col cols="3" class="mt-2 row-title">{{ t("Tag") }}</v-col>
                    <v-col cols="6">
                        <v-autocomplete
                            :value="activeCampaign.tag_id"
                            :items="tags"
                            :loading="loadingCampaignData"
                            class="text-field-item mb-1"
                            clearable
                            dense
                            hide-details
                            hide-no-data
                            hide-selected
                            item-text="name"
                            item-value="id"
                            outlined
                            @change="updateField($event, 'tag_id')"
                        />
                    </v-col>
                </v-row>
            </v-col>
        </v-row>
        <v-row no-gutters>
            <v-col cols="3" class="mt-2 row-title">{{ t("Start date") }}</v-col>
            <v-col cols="6">
                <date-picker v-model="startDate" :popover="{ placement: 'bottom', visibility: 'click' }">
                    <v-text-field
                        :value="getDate(startDate)"
                        append-icon="fas fa-calendar"
                        class="text-field-item mb-1"
                        dense
                        hide-details
                        outlined
                    />
                </date-picker>
            </v-col>
        </v-row>
        <v-row no-gutters>
            <v-col cols="3" class="mt-2 row-title">{{ t("End date") }}</v-col>
            <v-col cols="6">
                <date-picker v-model="endDate" :popover="{ placement: 'bottom', visibility: 'click' }">
                    <v-text-field
                        :value="getDate(endDate)"
                        append-icon="fas fa-calendar"
                        class="text-field-item mb-1"
                        dense
                        hide-details
                        outlined
                    />
                </date-picker>
            </v-col>
        </v-row>

        <v-row no-gutters>
            <v-col class="mt-2 row-title" cols="3">{{ t("Products") }}</v-col>
            <v-col cols="6">
                <v-autocomplete
                    @change="onSelect"
                    :items="unselectedProducts"
                    :item-text="item => `[${item.code}] ${item.name}`"
                    item-vasdlue="id"
                    multiple
                    hide-selected
                    hide-details
                    return-object
                    dense
                    outlined
                >
                    <template v-slot:selection=""></template>
                </v-autocomplete>
            </v-col>
        </v-row>
        <v-row no-gutters>
            <v-col cols="10">
                <v-simple-table class="mt-4" v-if="this.selectedProducts && selectedProducts.length > 0">
                    <template v-slot:default>
                        <thead>
                            <tr>
                                <th>{{ t("Product code") }}</th>
                                <th>{{ t("Product name") }}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="product in selectedProducts" :key="product.id">
                                <td>{{ product.code }}</td>
                                <td>{{ product.name }}</td>
                                <td align="right">
                                    <v-btn icon @click="handleDeleteCampaignProductClick(product.id)">
                                        <v-icon>delete</v-icon>
                                    </v-btn>
                                </td>
                            </tr>
                        </tbody>
                    </template>
                </v-simple-table>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import { mapActions, mapMutations, mapState } from "vuex";
import moment from "moment";
import DatePicker from "v-calendar/lib/components/date-picker.umd";

export default {
    name: "general-edit-tab",
    components: { DatePicker },
    data() {
        return {
            startDate: "",
            endDate: "",
            selectedProducts: []
        };
    },
    computed: {
        ...mapState(["products", "export_clients", "clients", "stores", "tags"]),
        ...mapState("CampaignStore", ["activeCampaign", "errorMessage", "loadingCampaignData", "infoProviders"]),
        unselectedProducts() {
            if (this.selectedProducts && this.selectedProducts.length > 0) {
                const selectedProductIds = this.selectedProducts.map(product => product.id);
                return this.products.filter(product => !selectedProductIds.includes(product.id));
            }
            return this.products;
        }
    },
    async mounted() {
        if (Object.keys(this.activeCampaign).length === 0) {
            await this.getCampaignData(this.$route.params.campaignId);
        }

        if (this.activeCampaign.products && this.activeCampaign.products.length > 0) {
            this.selectedProducts = this.activeCampaign.products;
        }
        if (this.activeCampaign.start_date) {
            this.startDate = await moment(this.activeCampaign.start_date, "DD.MM.YYYY").toDate();
        }
        if (this.activeCampaign.end_date) {
            this.endDate = await moment(this.activeCampaign.end_date, "DD.MM.YYYY").toDate();
        }
        await this.getFrequentlyUsedQueries({ products: true });
    },
    watch: {
        startDate: function(value) {
            this.updateField(moment(value).format("DD.MM.YYYY"), "start_date");
        },
        endDate: function(value) {
            this.updateField(moment(value).format("DD.MM.YYYY"), "end_date");
        },
        "activeCampaign.products": {
            handler(newProducts, oldProducts) {
                this.selectedProducts = this.activeCampaign.products;
            },
            deep: true,
            immediate: true
        }
    },
    methods: {
        ...mapActions(["getFrequentlyUsedQueries"]),
        ...mapMutations("CampaignStore", ["updateActiveCampaignProperty", "removeCampaignProduct"]),
        ...mapActions("CampaignStore", ["getCampaignData", "deleteCampaign", "deleteCampaignProduct"]),
        updateField(value, field) {
            this.updateActiveCampaignProperty({ prop: field, value: value });
        },
        getDate(value) {
            if (value) return moment(value).format("DD.MM.YYYY");
            else return null;
        },
        async handleDeleteCampaignProductClick(product_id) {
            if (!confirm(this.t("Are you sure you want to delete this campaign's product?"))) {
                return;
            }

            const productIndex = this.activeCampaign.products.findIndex(product => product.id === product_id);

            if (productIndex === -1) {
                return;
            }

            const product = this.activeCampaign.products[productIndex];

            if (product.product_type) {
                this.removeCampaignProduct(productIndex);
            } else {
                this.removeCampaignProduct(productIndex);
                await this.deleteCampaignProduct({
                    campaignId: this.activeCampaign.id,
                    productId: product_id
                });
            }
        },
        onSelect(selectedItems) {
            const existingIds = this.selectedProducts.map(item => item.id);
            const newItems = selectedItems.filter(item => !existingIds.includes(item.id));
            this.selectedProducts = [...this.selectedProducts, ...newItems];
            this.updateActiveCampaignProperty({ prop: "products", value: this.selectedProducts });
        }
    }
};
</script>

<style scoped>
.row-title {
    color: #888;
}
</style>
